import { z } from 'zod';

// ---- Disparo de notificação (Admin › Disparo de notificação) ------------------------------------

/**
 * Who a broadcast goes to. Everyone counted has notifications on in at least one device
 * (installed app or browser) and kept "Avisos da Invexa" on in Configurações; SELF is the
 * admin's own test and ignores that switch.
 */
export const PUSH_AUDIENCES = ['ALL', 'AFFILIATES', 'USER', 'SELF'] as const;
export type PushAudience = (typeof PUSH_AUDIENCES)[number];

export const PUSH_AUDIENCE_LABEL: Record<PushAudience, string> = {
  ALL: 'Todos',
  AFFILIATES: 'Afiliados',
  USER: 'Um usuário',
  SELF: 'Teste (só eu)',
};

export const PUSH_BROADCAST_STATUSES = ['SENDING', 'DONE', 'CANCELLED'] as const;
export type PushBroadcastStatus = (typeof PUSH_BROADCAST_STATUSES)[number];

export const PUSH_BROADCAST_STATUS_LABEL: Record<PushBroadcastStatus, string> = {
  SENDING: 'Enviando',
  DONE: 'Enviada',
  CANCELLED: 'Cancelada',
};

/** iPhone shows about 4 lines of body; longer texts are cut with "…" on every platform. */
export const PUSH_TITLE_MAX = 65;
export const PUSH_BODY_MAX = 240;

/** Pages a notification can open (tap). Any other platform path can be typed in. */
export const PUSH_LINK_PRESETS: { url: string; label: string }[] = [
  { url: '/operar', label: 'Operar' },
  { url: '/automacao', label: 'Automação' },
  { url: '/traderoom', label: 'Traderoom' },
  { url: '/estrategias', label: 'Estratégias' },
  { url: '/analise', label: 'Análise' },
  { url: '/historico', label: 'Histórico' },
  { url: '/aulas', label: 'Aulas' },
  { url: '/sugestoes', label: 'Sugestões' },
  { url: '/conta', label: 'Conta' },
  { url: '/afiliado', label: 'Painel do afiliado' },
];

/** Only pages of the platform: an outside site would take the installed app out of Invexa. */
const PUSH_LINK = /^\/(?!\/)[^\s\\]*$/;
export const isPushLink = (v: string) => PUSH_LINK.test(v.trim()) && v.trim().length <= 300;

/** Text as typed, minus NUL and invisible line noise; line breaks are kept (Android and iPhone show them). */
const pushText = (max: number, empty: string) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.replace(/\u0000/g, '').replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim() : v),
    z.string().min(1, empty).max(max, `Use no máximo ${max} caracteres.`),
  );

export const pushBroadcastAudienceSchema = z
  .object({
    audience: z.enum(PUSH_AUDIENCES),
    email: z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase() || undefined : v), z.email('E-mail inválido.').max(200).optional()),
  })
  .refine((a) => a.audience !== 'USER' || !!a.email, { message: 'Informe o e-mail do usuário.', path: ['email'] });

export const pushBroadcastInputSchema = z
  .object({
    title: z.preprocess((v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ') : v), pushText(PUSH_TITLE_MAX, 'Escreva o título.')),
    body: pushText(PUSH_BODY_MAX, 'Escreva a mensagem.'),
    url: z.string().trim().max(300).regex(PUSH_LINK, 'Use um caminho da plataforma, começando com / (ex.: /operar).').default('/operar'),
    audience: z.enum(PUSH_AUDIENCES),
    email: z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase() || undefined : v), z.email('E-mail inválido.').max(200).optional()),
    /** Random per form: a double click or a retried request never sends the same broadcast twice. */
    clientKey: z.string().regex(/^[A-Za-z0-9-]{8,64}$/),
  })
  .refine((a) => a.audience !== 'USER' || !!a.email, { message: 'Informe o e-mail do usuário.', path: ['email'] });
export type PushBroadcastInput = z.input<typeof pushBroadcastInputSchema>;

export interface PushAudienceDTO {
  /** Active devices that will get it. */
  devices: number;
  /** People behind those devices. */
  users: number;
  /** People in the audience with a device who turned "Avisos da Invexa" off. */
  optedOut: number;
  platforms: { ios: number; android: number; desktop: number };
  /** Only for USER: who the e-mail belongs to (null = no such active user). */
  user?: { name: string; email: string } | null;
}

export interface PushBroadcastDTO {
  id: string;
  title: string;
  body: string;
  url: string;
  audience: PushAudience;
  /** USER / SELF: who it went to. */
  target: { name: string; email: string } | null;
  status: PushBroadcastStatus;
  targetDevices: number;
  targetUsers: number;
  /** Accepted by Apple / Google / Mozilla push services. */
  sent: number;
  /** Push service refused or unreachable (after one retry). */
  failed: number;
  /** Device gone (app uninstalled, permission removed): turned off for good. */
  gone: number;
  author: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface PushBroadcastListDTO {
  items: PushBroadcastDTO[];
  nextCursor: string | null;
}
